import React from "react";
import { useTheme } from "react-jss";
import { useSelector } from "react-redux";
import type { Theme } from "../../../types/Theme";
import { kebabCase } from "../../../utils/lodash";
import useStyles from "./ChoiceListItemRenderer.styles";
import { RendererProps, getRendererProps } from "../BaseRenderer";
import { ChoiceContainer } from "../../../containers/controls/ChoiceContainer";
import { ChoiceModel } from "../../../models/ChoiceModel";
import ChoiceOpenEndRenderer from "./ChoiceOpenEndRenderer";
import { VariableModel } from "../../../models/VariableModel";
import { QuestionType, isChoiceList } from "../../../models/enums/QuestionType";
import { ChoiceRef } from "../../choices/BaseChoice";
import useQuestion from "../../../hooks/useQuestion";
import ChoiceLabelRenderer from "./ChoiceLabelRenderer";
import { ButtonChoice } from "../../shared/ButtonChoice";
import useTranslatable from "../../../hooks/useTranslatable";
import { getSettingString, getSettingNumber } from "../../../models/utilities/settingUtilities";
import { translate } from "../../../models/utilities/translationUtilities";
import { removeHtmlTags } from "../../../utils";
import { hasOpenEnd } from "../../../models/utilities/choiceUtilities";
import { StoreState } from "../../../models/states/StoreState";
import { SurveyQuestionnaireModality } from "../../../models/enums/SurveyQuestionnaireModality";
import { stripHtmlForAccessibleName } from "../../../utils/WCAGHelper";
import {
    getCarrouselScaleChoiceAriaLabelledBy,
    type CarrouselScaleAnchorLabelIds,
} from "./carrouselScaleA11y";

interface Props extends RendererProps {
    selected?: boolean;
    variable: VariableModel;
    isFirst?: boolean;
    isLast?: boolean;
    name?: string;
    gridRendering?: boolean;
    openEnd?: string;
    onOpenEndChange?(choice: ChoiceModel, text: string): void;
    onOpenEndBlur?(choice: ChoiceModel): void;
    choice?: ChoiceModel;
    horizontal?: boolean;
    controlType?: any;
    disabled?: boolean;
    onAfterSelection?(): void;
    executeUpdateForIdAfterSelection?: string;
    /** When true, carousel choices use checkbox semantics on the outer ButtonChoice wrapper. */
    isCarrouselCheckboxActive?: boolean;
    isCarrouselScaleActive?: boolean;
    /** True when carousel scale uses horizontal DisplayOrder and choices still fit on one row. */
    isCarrouselScaleHorizontalRow?: boolean;
    /** Modern collapsible grid panel: flat choice surface (no inset border shadow). */
    flatModernSurface?: boolean;
    choiceIndex?: number;
    choiceCount?: number;
    scaleAnchorLabelIds?: CarrouselScaleAnchorLabelIds;
}

const Choice = React.forwardRef(function ChoiceImpl(props: Props, ref: ChoiceRef) {
    const {
        choice,
        controlType,
        openEnd,
        isFirst,
        isLast,
        disabled,
        selected,
        variable,
        onOpenEndBlur,
        onOpenEndChange,
        language,
    } = props;

    const theme = useTheme();
    const classes = useStyles({ theme, selected });

    return (
        <>
            <div className={classes.choiceContainer}>
                <ChoiceContainer
                    {...getRendererProps(props)}
                    choiceRef={ref}
                    choice={choice}
                    controlType={controlType}
                    openEnd={openEnd}
                    isFirst={isFirst}
                    isLast={isLast}
                    isDisabled={disabled}
                    isSelected={selected}
                    variable={variable}
                />
            </div>
            <ChoiceOpenEndRenderer
                {...getRendererProps(props)}
                choice={choice}
                isSelected={selected}
                name={choice.elementId}
                label={removeHtmlTags(translate(choice, "TEXT", language))}
                value={openEnd}
                onOpenEndBlur={onOpenEndBlur}
                onOpenEndChange={onOpenEndChange}
                size={getSettingString(choice, "OpenEndSize", "Full")}
                lines={getSettingNumber(choice, "OpenEndLines", 2)}
            />
        </>
    );
});

export default function ChoiceListItemRenderer(props: Props) {
    const {
        choice,
        controlType,
        isFirst,
        isLast,
        disabled,
        openEnd,
        onOpenEndBlur,
        onOpenEndChange,
        gridRendering,
        selected,
        variable,
        horizontal,
        onAfterSelection,
        isCarrouselCheckboxActive,
        isCarrouselScaleActive,
        isCarrouselScaleHorizontalRow,
        choiceIndex = 0,
        choiceCount = 1,
        scaleAnchorLabelIds,
        language,
        flatModernSurface,
    } = props;
    const choiceRef: ChoiceRef = React.useRef();
    const question = useQuestion();
    const theme = useTheme() as Theme;
    const classes = useStyles({ theme, selected, horizontal });
    const baseChoiceLabel = removeHtmlTags(useTranslatable(choice, "TEXT"));
    const isTelephony = useSelector(
        (state: StoreState) =>
            state.interview.questionnaire?.modality === SurveyQuestionnaireModality.Phone,
    );
    const shouldDisplayCode =
        process.env.NODE_ENV !== 'test' &&
        isTelephony &&
        question !== undefined &&
        (question.type === QuestionType.RadioButton || question.type === QuestionType.CheckBox);
    const choiceLabel = shouldDisplayCode && choice.value ? `${choice.value} - ${baseChoiceLabel}` : baseChoiceLabel;

    const isCarrousel = question.type === QuestionType.Carrousel;
    const statementLabel = isCarrousel
        ? stripHtmlForAccessibleName(translate(variable, "text", language) || "")
        : "";
    const enrichedChoiceLabel = isCarrousel && statementLabel
        ? `${statementLabel}, ${choiceLabel}`
        : choiceLabel;

    if (!choice.value) {
        return (
            <div
                id={`${choice.elementId}_INPUT`}
                className={classes.choiceHeader}
                aria-labelledby={`${choice.elementId}_LABEL`}
            >
                <ChoiceLabelRenderer choice={choice} gridRendering={gridRendering} />
            </div>
        );
    }

    if (question.type === QuestionType.ImageSelector) {
        return <Choice ref={choiceRef} {...props} />;
    }

    const type = isChoiceList(question.type) ? question.type : QuestionType.RadioButton;

    const buttonChoiceRoleType =
        question.type === QuestionType.Carrousel && isCarrouselCheckboxActive
            ? "checkbox"
            : type === QuestionType.RadioButton
              ? "radio"
              : "checkbox";

    function onSelect() {
        if (choiceRef.current) {
            choiceRef.current.select();

            if (onAfterSelection) {
                onAfterSelection();
            }
        }
    }

    const openEndSize = getSettingString(choice, "OpenEndSize", "Medium");
    const carrouselChoiceBackgound = getSettingString(choice, "Color");

    const scaleAriaLabelledBy =
        isCarrouselScaleActive && isCarrousel
            ? getCarrouselScaleChoiceAriaLabelledBy(
                  choice,
                  choiceIndex,
                  choiceCount,
                  scaleAnchorLabelIds,
              )
            : undefined;

    return (
        <ButtonChoice
            id={`${choice.elementId}_INPUT`}
            choiceElementId={`${choice.elementId}`}
            disabled={disabled}
            type={buttonChoiceRoleType}
            label={enrichedChoiceLabel}
            ariaLabelledBy={scaleAriaLabelledBy}
            onSelect={onSelect}
            hasOpenEnd={hasOpenEnd(choice)}
            openEndSize={openEndSize}
            selected={selected}
            className={kebabCase(QuestionType[type])}
            horizontal={horizontal}
            renderChoice={() => (
                <ChoiceContainer
                    {...getRendererProps(props)}
                    choiceRef={choiceRef}
                    choice={choice}
                    controlType={controlType}
                    openEnd={openEnd}
                    isFirst={isFirst}
                    isLast={isLast}
                    isDisabled={disabled}
                    isSelected={selected}
                    variable={variable}
                />
            )}
            renderAfter={() => (
                <ChoiceOpenEndRenderer
                    {...getRendererProps(props)}
                    choice={choice}
                    isSelected={selected}
                    name={choice.elementId}
                    label={choiceLabel}
                    value={openEnd}
                    onOpenEndBlur={onOpenEndBlur}
                    onOpenEndChange={onOpenEndChange}
                    size={openEndSize}
                    lines={getSettingNumber(choice, "OpenEndLines", 2)}
                />
            )}
            carrouselChoiceBackgound={carrouselChoiceBackgound}
            variable={variable}
            isCarrouselScaleActive={isCarrouselScaleActive}
            isCarrouselScaleHorizontalRow={isCarrouselScaleHorizontalRow}
            flatModernSurface={flatModernSurface}
        />
    );
}
